import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './styles.module.scss';
import { formatMappingValue, isNumericString } from '@/utils/helpers';
import cloneDeep from 'lodash/cloneDeep';
import { produce } from 'immer';
import Dropdown from '@/components/dropdown';
import { lobFormModes } from '@certificate_hero/enums';
import uniqBy from 'lodash/uniqBy';
import { ErrorContext } from '@/components/lob-templates/context';
import { formPositions } from '@/enums/form-positions';
import ScheduleTable from '@/components/lob-template-forms/schedules/schedule-table';
import { LOCATION_COVERAGE_REGEX } from '@/constants';

const { ADDITIONAL, ADDITIONAL_SCHEDULES, CENTER } = formPositions;

/**
 * Matches `otherCoverageNameA` … `otherCoverageDeductibleJ`
 * (aligned with pdf-tools `sort-lobs-by-form` / `shouldMapBaseformLobsToOverflow`).
 */
export const OTHER_COVERAGE_FIELD_RE =
  /(^otherCoverage(?:Name|Deductible|Limit)[A-J])|physicalAddress$/;

const coverageFieldKey = field => field?.name || field?.label || '';

/** Map coverage `name` → field row for O(1) lookup when syncing schedules. */
export const buildOtherCoverageFieldMap = mappedFields => {
  const map = new Map();
  for (const field of mappedFields || []) {
    const key = coverageFieldKey(field);
    if (OTHER_COVERAGE_FIELD_RE.test(key)) {
      map.set(key, field);
    }
  }
  return map;
};

const hasOtherCoverageBaseSlots = fieldMap =>
  'ABCDEFGHIJ'
    .split('')
    .some(letter => fieldMap.has(`otherCoverageName${letter}`));

/** Property 101: all schedule rows are treated as overflow (same as current Acord 101). */
const NUM_OF_SCHEDULE_FIELDS = 0;

const isScheduleEmpty = schedule => !schedule.name && !schedule.value;

const normalizeScheduleForDisplay = ({ schedule, amsSchedules }) => {
  if (!schedule?.amsId) {
    return schedule;
  }

  const amsSchedule = amsSchedules?.find(s => s.amsId === schedule.amsId);

  if (
    amsSchedule?.name &&
    !schedule.overflowDeductName &&
    !schedule.overflowAmountName
  ) {
    const cleanedName = amsSchedule.name
      .replace(LOCATION_COVERAGE_REGEX, '')
      .trim();
    return {
      ...schedule,
      overflowDeductName: `${cleanedName} - Deductible`,
      overflowAmountName: `${cleanedName} - Amount`,
    };
  }

  return schedule;
};

const NAME_VALUE_FIELD_REGEX = /^(name|value)\s+([a-z])$/i;

const getLegacyNameValueSchedules = additionalFields => {
  const nameValueFields = additionalFields
    .map((field, index) => {
      const fieldName = field?.name || field?.label || '';
      const match = fieldName.match(NAME_VALUE_FIELD_REGEX);
      if (!match) {
        return null;
      }
      return {
        index,
        kind: match[1].toLowerCase(),
        letter: match[2].toUpperCase(),
        value: field?.value || '',
        amsId: field?.amsId || '',
        locationId: field?.locationId || '',
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);

  if (!nameValueFields.length) {
    return [];
  }

  const groupedByLetter = new Map();
  nameValueFields.forEach(field => {
    if (!groupedByLetter.has(field.letter)) {
      groupedByLetter.set(field.letter, {
        letter: field.letter,
        index: field.index,
        amsId: field.amsId,
        locationId: field.locationId,
        amountName: '',
        amountValue: '',
      });
    }
    const current = groupedByLetter.get(field.letter);
    if (field.kind === 'name') {
      current.amountName = field.value;
    } else {
      current.amountValue = field.value;
    }
  });

  const orderedPairs = Array.from(groupedByLetter.values()).sort(
    (a, b) => a.index - b.index
  );

  const schedules = [];
  for (let i = 0; i < orderedPairs.length; i += 2) {
    const amountPair = orderedPairs[i];
    const deductPair = orderedPairs[i + 1];
    schedules.push({
      amsId: amountPair?.amsId || deductPair?.amsId || '',
      locationId: amountPair?.locationId || deductPair?.locationId || '',
      name: '',
      value: String(amountPair?.amountValue || '').replace(/\$/g, ''),
      deductible: String(deductPair?.amountValue || '').replace(/\$/g, ''),
      overflowAmountName: amountPair?.amountName || '',
      overflowDeductName: deductPair?.amountName || '',
    });
  }

  return schedules;
};

/**
 * Build `schedule` (ADDITIONAL_SCHEDULES) rows in the same shape as Acord 101 useEffect3:
 * schedule-A / schedule-D with optional explicit overflow labels or `${name} - Amount|Deductible`.
 *
 * @param {Array<{ amsId?: string, name?: string, value?: string, deductible?: string, overflowAmountName?: string, overflowDeductName?: string, locationId?: string }>} overflowSchedules
 */
export const buildScheduleOverflowEntries = overflowSchedules =>
  overflowSchedules
    .map(schedule =>
      [
        {
          amsId: schedule.amsId,
          section: 'schedule-A',
          name:
            schedule.overflowAmountName ||
            (schedule.name ? `${schedule.name} - Amount` : ''),
          value: schedule.value,
          locationId: schedule.locationId,
        },
        {
          amsId: schedule.amsId,
          section: 'schedule-D',
          name:
            schedule.overflowDeductName ||
            (schedule.name ? `${schedule.name} - Deductible` : ''),
          value: schedule.deductible,
          locationId: schedule.locationId,
        },
      ].filter(s => !!s.value)
    )
    .flat();

const applyScheduleToOtherCoverageFields = (
  nameField,
  limitField,
  deductibleField,
  schedule
) => {
  if (!schedule?.amsId) {
    return;
  }

  const displayName = schedule.name || '';

  nameField.amsId = schedule.amsId || '';
  nameField.locationId = schedule.locationId || '';
  nameField.value = displayName;

  if (limitField) {
    limitField.amsId = schedule.amsId || '';
    limitField.locationId = schedule.locationId || '';
    limitField.value = isNumericString(schedule.value)
      ? `$${String(schedule.value).replace(/\$/g, '')}`
      : schedule.value || '';
  }
  if (deductibleField) {
    deductibleField.amsId = schedule.amsId || '';
    deductibleField.locationId = schedule.locationId || '';
    deductibleField.value = isNumericString(schedule.deductible)
      ? `$${String(schedule.deductible).replace(/\$/g, '')}`
      : schedule.deductible || '';
  }
};

const writeSchedulesToOtherCoverageSlots = (
  fieldByKey,
  schedulesToUpdate,
  getAmsValue
) => {
  for (let charCode = 65; charCode <= 74; charCode++) {
    const letter = String.fromCharCode(charCode);
    const nameField = fieldByKey.get(`otherCoverageName${letter}`);
    const limitField = fieldByKey.get(`otherCoverageLimit${letter}`);
    const deductibleField = fieldByKey.get(`otherCoverageDeductible${letter}`);

    if (!nameField || !limitField) {
      continue;
    }

    const schedule = schedulesToUpdate.shift();
    if (!schedule) {
      break;
    }

    if (schedule.amsId && !schedule.name) {
      schedule.name = getAmsValue(schedule.amsId, 'name');
    }

    applyScheduleToOtherCoverageFields(
      nameField,
      limitField,
      deductibleField,
      schedule
    );
  }
};

const clearOtherCoverageSlot = (nameField, limitField, deductibleField) => {
  if (nameField) {
    nameField.value = '';
    nameField.amsId = '';
    nameField.locationId = '';
  }
  if (limitField) {
    limitField.value = '';
    limitField.amsId = '';
    limitField.locationId = '';
  }
  if (deductibleField) {
    deductibleField.value = '';
    deductibleField.amsId = '';
    deductibleField.locationId = '';
  }
};

const syncOverflowScheduleValuesToSave = (
  draft,
  overflowSchedules,
  getAmsValue
) => {
  const existing = draft[ADDITIONAL_SCHEDULES] || [];

  for (const schedule of overflowSchedules) {
    if (!schedule?.amsId || isScheduleEmpty(schedule)) {
      continue;
    }

    const plainName =
      schedule.name ||
      (getAmsValue(schedule.amsId, 'name') !== 'Not Found'
        ? getAmsValue(schedule.amsId, 'name')
        : '');

    const amountEntry = existing.find(
      entry => entry.amsId === schedule.amsId && entry.section === 'schedule-A'
    );
    const deductEntry = existing.find(
      entry => entry.amsId === schedule.amsId && entry.section === 'schedule-D'
    );

    if (amountEntry) {
      amountEntry.value = schedule.value;
      amountEntry.locationId = schedule.locationId || amountEntry.locationId;
    } else if (schedule.value) {
      existing.push({
        amsId: schedule.amsId,
        section: 'schedule-A',
        name: plainName,
        value: schedule.value,
        locationId: schedule.locationId,
      });
    }

    if (deductEntry) {
      deductEntry.value = schedule.deductible;
      deductEntry.locationId = schedule.locationId || deductEntry.locationId;
    } else if (schedule.deductible) {
      existing.push({
        amsId: schedule.amsId,
        section: 'schedule-D',
        name: plainName,
        value: schedule.deductible,
        locationId: schedule.locationId,
      });
    }
  }
};

/**
 * Syncs schedule rows to the save-oriented field mappings ref without applying
 * display-only `- Amount` / `- Deductible` name remapping. Removes deselected
 * schedules and updates limit/deductible values on existing coverage entries.
 */
export const syncSchedulesToSaveFieldMappings = (
  draft,
  schedules,
  getAmsValue
) => {
  const activeAmsIds = new Set(
    schedules.filter(schedule => schedule.amsId).map(schedule => schedule.amsId)
  );

  draft[CENTER] = (draft[CENTER] || []).filter(fieldMapping => {
    if (!fieldMapping.amsId) {
      return true;
    }

    const fieldKey = coverageFieldKey(fieldMapping);
    if (OTHER_COVERAGE_FIELD_RE.test(fieldKey)) {
      return true;
    }

    return activeAmsIds.has(fieldMapping.amsId);
  });

  const mappedFields = draft[CENTER];
  const schedulesToUpdate = cloneDeep(schedules);

  if (mappedFields?.length) {
    const fieldByKey = buildOtherCoverageFieldMap(mappedFields);
    if (hasOtherCoverageBaseSlots(fieldByKey)) {
      for (let charCode = 65; charCode <= 74; charCode++) {
        const letter = String.fromCharCode(charCode);
        const nameField = fieldByKey.get(`otherCoverageName${letter}`);
        const limitField = fieldByKey.get(`otherCoverageLimit${letter}`);
        const deductibleField = fieldByKey.get(
          `otherCoverageDeductible${letter}`
        );

        if (nameField?.amsId && !activeAmsIds.has(nameField.amsId)) {
          clearOtherCoverageSlot(nameField, limitField, deductibleField);
        }
      }
      writeSchedulesToOtherCoverageSlots(
        fieldByKey,
        schedulesToUpdate,
        getAmsValue
      );
    }
  }

  syncOverflowScheduleValuesToSave(draft, schedulesToUpdate, getAmsValue); // or here?
};

const mergeOverflowAndLegacyIntoSchedules = (
  schedulesMap,
  scheduleOverflow,
  additionalForLegacy
) => {
  const overflowFields = scheduleOverflow || [];
  for (let i = 0; i < overflowFields.length; i++) {
    const amsId = overflowFields[i]?.amsId;
    const locationId = overflowFields[i]?.locationId;
    if (!amsId) {
      continue;
    }
    const existingSchedule = schedulesMap.get(amsId) || {
      amsId,
      locationId,
      name: '',
      value: '',
      deductible: '',
    };

    if (overflowFields[i]?.section === 'schedule-A') {
      existingSchedule.overflowAmountName = overflowFields[i].name;
      existingSchedule.value = overflowFields[i].value;
    } else if (overflowFields[i]?.section === 'schedule-D') {
      existingSchedule.overflowDeductName = overflowFields[i].name;
      existingSchedule.deductible = overflowFields[i].value;
    }

    schedulesMap.set(amsId, existingSchedule);
  }

  if (overflowFields.length === 0) {
    getLegacyNameValueSchedules(additionalForLegacy || []).forEach(
      (legacySchedule, idx) => {
        const key = legacySchedule.amsId || `legacy-schedule-${idx}`;
        schedulesMap.set(key, legacySchedule);
      }
    );
  }

  return Array.from(schedulesMap.values());
};

const parseSchedulesFromOtherCoverageFieldMap = ({
  fieldByKey,
  amsSchedules,
}) => {
  const schedulesMap = new Map();

  for (let charCode = 65; charCode <= 74; charCode++) {
    const letter = String.fromCharCode(charCode);
    const nameField = fieldByKey.get(`otherCoverageName${letter}`);
    const limitField = fieldByKey.get(`otherCoverageLimit${letter}`);
    const deductibleField = fieldByKey.get(`otherCoverageDeductible${letter}`);

    if (!nameField || !limitField || !deductibleField) {
      continue;
    }

    const name = nameField.value || '';
    const value = limitField.value || '';
    const deductible = deductibleField.value || '';

    const amsSchedule = (amsSchedules || []).find(s => {
      return s.name?.replace(/\s+/g, ' ') === String(name).replace(/\s+/g, ' ');
    });

    const amsId = nameField.amsId || amsSchedule?.amsId || '';
    const locationId = nameField.locationId || amsSchedule?.locationId || '';

    const schedule = {
      amsId,
      locationId,
      name,
      value: String(value || '').replace(/\$/g, ''),
      deductible: String(deductible || '').replace(/\$/g, ''),
    };

    if (!isScheduleEmpty(schedule)) {
      const key = amsId || `${name}-${value}-${deductible}`;
      schedulesMap.set(key, schedule);
    }
  }

  return schedulesMap;
};

/**
 * Hydrate UI schedule rows from `ADDITIONAL` (`otherCoverage*A-J` or legacy name/limit/deductible
 * triplets) plus `ADDITIONAL_SCHEDULES` overflow rows.
 */
export const parseSchedulesFromAdditionalMappings = ({
  additionalTriples,
  scheduleOverflow,
  amsSchedules,
}) => {
  const fieldByKey = buildOtherCoverageFieldMap(additionalTriples || []);

  if (hasOtherCoverageBaseSlots(fieldByKey)) {
    const schedulesMap = parseSchedulesFromOtherCoverageFieldMap({
      fieldByKey,
      amsSchedules,
    });
    return mergeOverflowAndLegacyIntoSchedules(
      schedulesMap,
      scheduleOverflow,
      additionalTriples
    );
  }

  const schedulesMap = new Map();

  for (let i = 0; i < (additionalTriples || []).length; i += 3) {
    const name = additionalTriples[i]?.value || '';
    const value = additionalTriples[i + 1]?.value || '';
    const deductible = additionalTriples[i + 2]?.value || '';

    const amsSchedule = (amsSchedules || []).find(s => {
      return s.name?.replace(/\s+/g, ' ') === String(name).replace(/\s+/g, ' ');
    });

    const amsId = additionalTriples[i]?.amsId || amsSchedule?.amsId || '';
    const locationId =
      additionalTriples[i]?.locationId || amsSchedule?.locationId || '';

    const schedule = {
      amsId,
      locationId,
      name,
      value: String(value || '').replace(/\$/g, ''),
      deductible: String(deductible || '').replace(/\$/g, ''),
    };

    if (!isScheduleEmpty(schedule)) {
      const key = amsId || `${name}-${value}-${deductible}`;
      schedulesMap.set(key, schedule);
    }
  }

  return mergeOverflowAndLegacyIntoSchedules(
    schedulesMap,
    scheduleOverflow,
    additionalTriples
  );
};

const scheduleOnFormToFieldMappingOverflowFormat = schedule => {
  const common = {
    amsId: schedule.amsId || '',
    locationId: schedule.locationId || '',
  };
  return [
    {
      ...common,
      name: schedule.overflowAmountName || `${schedule.name} - Amount`,
      section: 'schedule-A',
      value: schedule.value,
    },
    {
      ...common,
      name: schedule.overflowDeductibleName || `${schedule.name} - Deductible`,
      section: 'schedule-D',
      value: schedule.deductible,
    },
  ];
};

const scheduleOnFormToFieldMappingBaseFormFormat = (schedule, letter) => {
  const common = {
    amsId: schedule.amsId || '',
    locationId: schedule.locationId || '',
  };

  return [
    {
      ...common,
      name: `otherCoverageName${letter}`,
      label: `Name ${letter}`,
      type: 'ATTRIBUTE',
      value: schedule.name || '',
    },
    {
      ...common,
      name: `otherCoverageLimit${letter}`,
      label: `Value ${letter}`,
      type: 'LIMIT',
      value: schedule.value || '',
    },
    {
      ...common,
      name: `otherCoverageDeductible${letter}`,
      label: `Deductible ${letter}`,
      type: 'DEDUCTIBLE',
      value: schedule.deductible || '',
    },
  ];
};

const scheduleOnFormToFieldMapping = (schedule, letter) => {
  if (letter.charCodeAt(0) >= 65 && letter.charCodeAt(0) <= 74) {
    return scheduleOnFormToFieldMappingBaseFormFormat(schedule, letter);
  }
  return scheduleOnFormToFieldMappingOverflowFormat(schedule);
};

const Acord101Schedules = ({
  fieldMappings: saveFieldMappings,
  amsSchedules,
  currentMode,
  clearSchedulesForm,
  setClearSchedulesForm,
}) => {
  const [, setErrors] = useContext(ErrorContext);
  const [allSchedulesOnForm, setAllSchedulesOnFormBase] = useState([]);
  const [selectedScheduleOptions, setSelectedScheduleOptions] = useState(() =>
    uniqBy(
      (saveFieldMappings?.current[ADDITIONAL] || [])
        .concat(saveFieldMappings?.current[ADDITIONAL_SCHEDULES] || [])
        .map(({ amsId }) => ({ id: amsId }))
        .filter(({ id }) => !!id),
      'id'
    )
  );
  const isInitialDataLoadedRef = useRef(false);
  const schedulesRef = useRef(allSchedulesOnForm);

  useEffect(() => {
    if (
      !isInitialDataLoadedRef.current ||
      !saveFieldMappings?.current?.[CENTER]
    ) {
      return;
    }
    const amsIdsInFieldMappings = saveFieldMappings?.current?.[CENTER]?.map(
      field => field.amsId
    );

    saveFieldMappings.current = produce(saveFieldMappings.current, draft => {
      draft[ADDITIONAL_SCHEDULES] = [];
      draft[ADDITIONAL] = [];
    });

    let i = 0;
    for (const schedule of allSchedulesOnForm) {
      if (schedule.amsId && !amsIdsInFieldMappings.includes(schedule.amsId)) {
        saveFieldMappings.current = produce(
          saveFieldMappings.current,
          draft => {
            const letter = String.fromCharCode(i + 65);
            draft[CENTER].push(
              ...scheduleOnFormToFieldMapping(schedule, letter)
            );
          }
        );
      }
      i++;
    }
  }, [saveFieldMappings, allSchedulesOnForm, isInitialDataLoadedRef]);

  // const displayScheduleState = useCallback(
  //   calling_location => {
  //     console.log(`displayScheduleState called from: ${calling_location}`);
  //     console.log('selectedScheduleOptions', selectedScheduleOptions);
  //     console.log('allSchedulesOnForm', allSchedulesOnForm);
  //     console.log('schedulesRef.current', schedulesRef.current);
  //     console.log('saveFieldMappings.current', saveFieldMappings.current);
  //   },
  //   [
  //     selectedScheduleOptions,
  //     allSchedulesOnForm,
  //     schedulesRef,
  //     saveFieldMappings,
  //   ]
  // );
  // displayScheduleState('Acord101Schedules render');

  const getAmsValue = useCallback(
    (amsId, key) => {
      if (!amsId) {
        return 'Not Found';
      }

      const amsSchedule = amsSchedules.find(s => s.amsId === amsId);

      if (!amsSchedule || !amsSchedule[key]) {
        return 'Not Found';
      }
      return formatMappingValue(amsSchedule[key]);
    },
    [amsSchedules]
  );

  const scheduleOptions = useMemo(
    () =>
      (amsSchedules || []).map(schedule => ({
        id: schedule.amsId,
        text: schedule.name,
      })),
    [amsSchedules]
  );

  const hasFieldMappings = !!saveFieldMappings?.current?.[CENTER];

  const clearCoverageErrors = useCallback(() => {
    setErrors(prev => {
      const next = { ...prev };
      delete next.coverage;
      return next;
    });
  }, [setErrors]);

  const syncSaveFieldMappingsFromSchedules = useCallback(
    nextSchedules => {
      saveFieldMappings.current = produce(saveFieldMappings.current, draft => {
        syncSchedulesToSaveFieldMappings(draft, nextSchedules, getAmsValue);
      });
      clearCoverageErrors();
    },
    [saveFieldMappings, getAmsValue, clearCoverageErrors]
  );

  /**
   * Keeps save-oriented `fieldMappings` in sync without a dedicated `useEffect` on
   * `allSchedulesOnForm`. Display rows use `allSchedulesOnForm` with normalized
   * `- Amount` / `- Deductible` labels via {@link normalizeScheduleForDisplay}.
   */
  const setAllSchedulesOnForm = useCallback(
    update => {
      const prev = schedulesRef.current;
      const next = typeof update === 'function' ? update(prev) : update;
      schedulesRef.current = next;

      if (isInitialDataLoadedRef.current) {
        syncSaveFieldMappingsFromSchedules(next);
      }
      setAllSchedulesOnFormBase(next);
    },
    [syncSaveFieldMappingsFromSchedules]
  );

  useEffect(() => {
    if (!(hasFieldMappings && currentMode)) {
      return;
    }

    const mappedFromFields = parseSchedulesFromAdditionalMappings({
      additionalTriples: saveFieldMappings.current[ADDITIONAL] || [],
      scheduleOverflow: saveFieldMappings.current[ADDITIONAL_SCHEDULES] || [],
      amsSchedules,
    });

    const mappedSchedules = mappedFromFields.map(schedule =>
      normalizeScheduleForDisplay({ schedule, amsSchedules })
    );

    schedulesRef.current = mappedSchedules;
    setAllSchedulesOnFormBase(mappedSchedules);
    isInitialDataLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode, saveFieldMappings, hasFieldMappings, amsSchedules]);

  useEffect(() => {
    if (selectedScheduleOptions.length === 0) {
      setAllSchedulesOnForm(current => {
        if (!current) {
          return [];
        }
        const customSchedules = current.filter(
          schedule => !schedule.amsId && !isScheduleEmpty(schedule)
        );

        if (customSchedules.length > 0) {
          return customSchedules;
        }

        return [
          {
            amsId: '',
            name: '',
            value: '',
            deductible: '',
            locationId: '',
          },
        ];
      });
      return;
    }

    setAllSchedulesOnForm(current => {
      const idsOnForm = current.length ? current.map(s => s.amsId) : [];
      const selectedOptionIds = selectedScheduleOptions.map(o => o.id);

      const updatedSchedules = current.filter(
        schedule =>
          !schedule.amsId || selectedOptionIds.includes(schedule.amsId)
      );

      selectedScheduleOptions.forEach(option => {
        if (!idsOnForm.includes(option.id)) {
          const schedule = amsSchedules.find(s => s.amsId === option.id);
          const blankSpace = updatedSchedules.find(isScheduleEmpty);
          const updatedSchedule = blankSpace || {};

          updatedSchedule.amsId = schedule?.amsId;
          updatedSchedule.name = schedule?.name
            ?.replace(LOCATION_COVERAGE_REGEX, '')
            ?.trim();
          updatedSchedule.value = schedule?.value;
          updatedSchedule.deductible = schedule?.deductible;
          updatedSchedule.locationId = schedule?.locationId;

          const emptySchedule = !updatedSchedule.name && !updatedSchedule.value;

          if (blankSpace || emptySchedule) {
            return;
          }

          updatedSchedules.push(updatedSchedule);
        }
      });

      return updatedSchedules
        .map(schedule =>
          normalizeScheduleForDisplay({ schedule, amsSchedules })
        )
        .filter(schedule => selectedOptionIds.includes(schedule.amsId));
    });
  }, [
    amsSchedules,
    selectedScheduleOptions,
    scheduleOptions,
    currentMode,
    setAllSchedulesOnForm,
  ]);

  // useEffect(() => {
  //   if (!clearSchedulesForm) {
  //     return;
  //   }

  //   const current = schedulesRef.current;
  //   const customSchedules = current.filter(
  //     schedule => !schedule.amsId && !isScheduleEmpty(schedule)
  //   );

  //   let nextSchedules;
  //   if (customSchedules.length > 0) {
  //     nextSchedules = [...customSchedules];
  //   }

  //   schedulesRef.current = nextSchedules;
  //   setAllSchedulesOnFormBase(nextSchedules);
  //   setSelectedScheduleOptions([]);
  //   fieldMappings.current = produce(fieldMappings.current, draft => {
  //     draft[ADDITIONAL] = (draft[ADDITIONAL] || []).map(mapping => ({
  //       ...mapping,
  //       value: mapping.amsId ? '' : mapping.value,
  //       amsId: '',
  //       locationId: mapping.amsId ? '' : mapping.locationId,
  //     })); // remove any additional fields with an amsId

  //     draft[ADDITIONAL_SCHEDULES] = (draft[ADDITIONAL_SCHEDULES] || []).filter(
  //       schedule => !schedule.amsId
  //     );
  //   });

  //   setClearSchedulesForm(false);
  // }, [clearSchedulesForm, fieldMappings, setClearSchedulesForm, currentMode]);

  const handleScheduleSelectionChange = selectedItems => {
    if (selectedItems.length === 0) {
      setClearSchedulesForm(true);
    }
    setSelectedScheduleOptions(selectedItems);
  };

  return (
    <div className={styles.schedulesContainer}>
      <Dropdown
        className={styles.schedulesDropDown}
        multiSelect
        selectAllButton
        name="schedulesDropDown"
        label="101 Schedules"
        placeholder="SELECT SCHEDULE"
        options={scheduleOptions}
        disabled={currentMode !== lobFormModes.MANAGER}
        selectedItems={(allSchedulesOnForm || [])
          .map(({ amsId = '', name }) => ({
            id: amsId,
            text: name || getAmsValue(amsId, 'name'),
          }))
          .filter(({ id }) => !!id)}
        setSelectedItems={handleScheduleSelectionChange}
      />

      <ScheduleTable
        allSchedulesOnForm={allSchedulesOnForm || []}
        getAmsValue={getAmsValue}
        amsSchedules={amsSchedules}
        setAllSchedulesOnForm={setAllSchedulesOnForm}
        currentMode={currentMode}
        numberOfSchedules={NUM_OF_SCHEDULE_FIELDS}
        setSelectedScheduleOptions={setSelectedScheduleOptions}
        isExplicitlyOverflow={true}
      />
    </div>
  );
};

export default Acord101Schedules;
